import React, {useRef, useState} from 'react';
import cx from 'classnames';

import type {InteractiveElementProps, IconSource} from '@pexip/components';
import {
    Icon,
    IconTypes,
    Color,
    Tooltip,
    InteractiveElement,
    FileUpload,
    onEnter,
} from '@pexip/components';

import styles from './EffectItem.module.scss';

export const EffectItem: React.FC<
    InteractiveElementProps & {
        icon?: IconSource;
        imageUrl?: string;
        isSelected?: boolean;
        effectName?: string;
        showTooltip?: boolean;
        tooltipDelay?: number;
        isImageError?: boolean;
        onFileUploadChange?: React.ChangeEventHandler<HTMLInputElement>;
        acceptedImageFormats?: string;
        size?:
            | 'smallSquare'
            | 'largeSquare'
            | 'largePortrait'
            | 'smallLandscape';
        endContent?: (isHovering: boolean) => React.ReactElement | null;
        onClick?: () => void;
    }
> = ({
    isSelected = false,
    isImageError = false,
    showTooltip = true,
    tooltipDelay = 1000,
    size = 'smallSquare',
    icon,
    acceptedImageFormats,
    onFileUploadChange,
    imageUrl,
    className,
    children,
    effectName,
    endContent,
    onClick,
    ...props
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const isImage = !!imageUrl;
    const isFileUpload = !!onFileUploadChange;
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const content = (
        <InteractiveElement
            onClick={onClick}
            tabIndex={0}
            role="button"
            onKeyDown={onEnter(() => {
                onClick?.();

                if (isFileUpload && fileUploadRef.current) {
                    fileUploadRef.current.click();
                }
            })}
            htmlTag={isFileUpload || isImage ? 'div' : 'button'}
            aria-label={effectName}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            data-active={isSelected}
            className={cx(
                'align-items-center',
                'd-flex',
                'flex-direction-column',
                'justify-content-center',
                styles.effectItem,
                {
                    [styles.smallSquare]: size === 'smallSquare',
                    [styles.largeSquare]: size === 'largeSquare',
                    [styles.largePortrait]: size === 'largePortrait',
                    [styles.smallLandscape]: size === 'smallLandscape',
                    [styles.isSelected]: isSelected,
                    [styles.isImage]: isImage,
                    [styles.isImageError]: isImageError,
                },
                className,
            )}
            {...(imageUrl && !isImageError
                ? {
                      style: {
                          backgroundImage: `url('${imageUrl}')`,
                      },
                  }
                : {})}
            {...props}
        >
            {children}
            {icon && <Icon source={icon} />}
            {isImageError && (
                <Icon color={Color.Red70} source={IconTypes.IconWarningRound} />
            )}
            {endContent?.(isHovering)}
        </InteractiveElement>
    );

    return !isImageError ? (
        <Tooltip
            text={effectName}
            isTooltipEnabled={showTooltip}
            delay={tooltipDelay}
        >
            {isFileUpload ? (
                <FileUpload
                    accept={acceptedImageFormats}
                    onChange={onFileUploadChange}
                    multiple={false}
                    colorScheme="light"
                    ref={fileUploadRef}
                    noFocus
                >
                    {content}
                </FileUpload>
            ) : (
                content
            )}
        </Tooltip>
    ) : (
        content
    );
};

export type EffectItemProps = React.ComponentProps<typeof EffectItem>;
